import _ from 'lodash';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import APIs from '../../api/api';
import {
    Channel,
    ChannelsUsersType,
    GlobalSearchResponse,
    MessageType,
    RawMessage,
    SearchResponse,
    SendMessage,
} from '../../types/channel';
import { pollMessage, pollMessageInitial, TypeImageFile } from '../../types/messageType';
import { Operation } from '../../utils/Operation';
import { AppRootStore } from '../store';

const initialMassegeText: SendMessage = {
    type: 'text',
    minRelevance: -1,
    isReply: false,
    hashtags: [],
    taggedUserId: 0,
    mentionedUserIds: [0],
    mediaTitle: undefined,
    mediaUrl: undefined,
    msgLocation: undefined,
    mediaWidth: 0,
    mediaHeight: 0,
    duration: 0,
};

type FileUploadOperationData = {
    filePath: string;
    fileTitle: string;
    thumbnailPath: string;
};

type MessageCacheType = {
    [key: string]: {
        messages: RawMessage[];
        pageState: string | null;
        end?: boolean;
        channelData: Channel;
        channelUsers: {
            [key: string]: ChannelsUsersType;
        };
    };
};

type MessagesInChannelType = {
    messages: RawMessage[];
    pageState: string | null;
    end?: boolean;
};

type unreadMessagesType = {
    slug: string;
    count: number;
};

type CacheMessagesType = {
    messages: Map<string, RawMessage>;
    pageState: string | null;
    end?: boolean;
};

export default class MessageStore {
    app: AppRootStore;
    constructor(app: AppRootStore) {
        makeAutoObservable(this);
        this.app = app;
    }

    channelsMsgDataBySlug = new Map<string, CacheMessagesType>();
    slugQueue = new Set<string>();
    newMsgChannelsSlug = '';

    fileType: string = '';

    FileUploadOperation = new Operation<FileUploadOperationData>({
        filePath: '',
        fileTitle: '',
        thumbnailPath: '',
    });

    getPollOperation = new Operation({});

    searchMessageId: string = '';

    slug: string = '';
    messageCache: MessageCacheType = {};

    unreadMessages: unreadMessagesType[] = [];

    prevInnerDivHeight: {
        [key: string]: number;
    } = {};

    messagesInChannel: MessagesInChannelType = {
        messages: [],
        pageState: null,
        end: false,
    };
    dataInChannel: Channel | null = null;
    usersInChannel: {
        [key: string]: ChannelsUsersType;
    } = {};

    searchMessageState: string = '';

    searchMessages: SearchResponse = {
        messages: [],
        count: 0,
        isEnd: false,
    };

    globalSearchMessages: GlobalSearchResponse = {
        messages: [],
        count: 0,
        pageState: null,
        isEnd: false,
    };

    durationMedia: number = 0;

    messageTextState: string = '';
    messageImageTextState: string = '';

    isLoadMessages: boolean = false;

    setSendMessage: SendMessage = initialMassegeText;

    setReplyMessage: RawMessage | null = null;

    editMessage: RawMessage | null = null;
    addHashtag: RawMessage | null = null;

    progress: number = 0;
    isMessagesLength: boolean = false;

    setSendReplyMessageState = {};

    minRelevance: number = -1;
    goToBottom = false;
    messagesFilterValue: number = 0;
    pollMessageState: pollMessage = pollMessageInitial;
    pollMessageOptionState: string[] = ['', ''];
    pollMessageDetails: pollMessage = {} as never;

    upsertChannelsMsgs = (slug: string, data: MessagesInChannelType) => {
        const msgs = this.channelsMsgDataBySlug.get(slug);
        if (msgs) {
            data.messages.forEach(msg => {
                msgs.messages.set(msg.id, msg);
            });
            msgs.pageState = data.pageState;
            msgs.end = data.end;
        } else {
            const messageById = new Map<string, RawMessage>();
            data.messages.forEach(msg => {
                messageById.set(msg.id, msg);
            });
            this.channelsMsgDataBySlug.set(slug, {
                messages: messageById,
                pageState: data.pageState,
                end: data.end,
            });
        }
    };

    get getSelectedChannelMsgData() {
        return this.channelsMsgDataBySlug.get(this.app.channelStore.selectedChannelData.slug);
    }

    get getBySlugChannelMsgData() {
        return this.channelsMsgDataBySlug.get(this.newMsgChannelsSlug);
    }

    get getSelectedChannelMsgsMap() {
        return this.getSelectedChannelMsgData?.messages || new Map<string, RawMessage>();
    }

    get getSelectedChannelMsgsPageState() {
        return this.getSelectedChannelMsgData?.pageState || '';
    }

    get getSelectedChannelMsgsEnd() {
        return this.getSelectedChannelMsgData?.end || false;
    }

    get getSelectedChannelMsgs() {
        return Array.from(this.getSelectedChannelMsgsMap.values());
    }

    set setChannelMsgNewMessage(msg: RawMessage) {
        if (msg.channelSlug !== this.app.channelStore.selectedChannelData.slug) {
            this.setBySlugChannelMsgNewMessage = msg;
        } else if (
            this.getSelectedChannelMsgData?.messages &&
            msg.channelSlug == this.app.channelStore.selectedChannelData.slug
        ) {
            const messages = Array.from(this.getSelectedChannelMsgData?.messages);
            messages.unshift([msg.id, msg]);
            this.getSelectedChannelMsgData.messages = new Map(messages);
        }
    }

    set setBySlugChannelMsgNewMessage(msg: RawMessage) {
        if (this.getBySlugChannelMsgData?.messages) {
            const messages = Array.from(this.getBySlugChannelMsgData?.messages);
            messages.unshift([msg.id, msg]);
            this.getBySlugChannelMsgData.messages = new Map(messages);
        }
    }

    set setChannelMessageByMessageId(msg: RawMessage) {
        this.getSelectedChannelMsgData?.messages.set(msg.id, msg);
    }

    getChannelMsgDataBySlug(slug: string) {
        return this.channelsMsgDataBySlug.get(slug);
    }

    getSelectedChannelMsgById = (id: string) => {
        return this.getSelectedChannelMsgsMap.get(id);
    };

    set setSelectedChannelMsgById(msg: RawMessage) {
        this.getSelectedChannelMsgsMap.set(msg.id, msg);
    }

    setIsMessagesLength = (is: boolean) => {
        runInAction(() => {
            this.isMessagesLength = is;
        });
    };

    setPollMessageState = (key: keyof pollMessage, value: any) => {
        runInAction(() => {
            this.pollMessageState[key] = value as never;
        });
    };

    setSearchMessageId = (value: string) => {
        runInAction(() => {
            this.searchMessageId = value;
        });
    };

    setChangePollOption = (e: any, index: number) => {
        runInAction(() => {
            this.pollMessageOptionState[index] = e;
            this.pollMessageOptionState = [...this.pollMessageOptionState];
        });
    };

    addPollOption = () => {
        runInAction(() => {
            if (this.pollMessageOptionState.length < 10) {
                let item = '';
                this.pollMessageOptionState.push(item);
                this.setPollMessageState('options', this.pollMessageOptionState);
            }
        });
    };

    removePollOption = (v: string, index: number) => {
        runInAction(() => {
            if (this.pollMessageOptionState.length > 2)
                this.pollMessageOptionState = this.pollMessageOptionState.filter((e, i) => i !== index);
        });
    };

    clearPollMessageState = () => {
        runInAction(() => {
            this.pollMessageState = pollMessageInitial;
            this.pollMessageOptionState = ['', ''];
        });
    };

    votePollOption = (
        pollOption: number,
        channelSlug: string,
        pollId: number,
        messageId: string,
        // callback: () => void
    ) => {
        this.app.chatStore.vote(
            pollOption,
            channelSlug,
            pollId,
            messageId,
            // callback
        );
    };

    setMessageFilterValue = _.debounce(
        (value: number) => {
            runInAction(() => {
                this.messagesFilterValue = value;
            });
        },
        400,
        { leading: false, trailing: true },
    );

    setPrevInnerDivHeight = (slug: string, height: number) => {
        this.prevInnerDivHeight[slug] = height;
    };

    setDurationMedia = (value: number) => {
        console.log({ value });
        this.durationMedia = value;
    };

    setSearch = (text: string) => {
        runInAction(() => {
            this.searchMessageState = text;
        });
    };

    clearSearch = () => {
        runInAction(() => {
            this.searchMessageState = '';
            this.searchMessages = {
                count: 0,
                isEnd: false,
                messages: [],
            };
        });
    };

    setChannelSlug = (slug: string) => {
        runInAction(() => {
            this.app.hashtagStore.allChatHashTags = [];
            this.slug = slug;
        });
        if (this.slug === slug) {
            this.minRelevance = -1;
        }
    };

    getHistoryMessages = (slug: string) => {
        this.app.chatStore.history({
            slug,
        });

        this.app.socketStore.socket?.on(
            'history',
            (data: { messages: RawMessage[]; pageState: string; end: boolean }) => {
                runInAction(() => {
                    if (this.app.hashtagStore.isOpenHashTagScreen) {
                        this.app.hashtagStore.setAllHashTagsMessages(data);
                    } else {
                        this.setHistoryMessages(
                            data.messages[0]?.channelSlug || slug,
                            data.messages,
                            data.pageState,
                            data.end,
                        );
                    }
                    setInterval(() => {
                        runInAction(() => {
                            this.isLoadMessages = false;
                        });
                    }, 300);
                });
            },
        );
    };

    getHistoryMessagesPageState = (setIsFetching, stop) => {
        if (this.messageCache[this.slug]?.end === false) {
            this.app.chatStore.history({
                slug: this.slug,
                pageState: this.getMessagesPageState(this.slug),
            });
            setIsFetching(false);
        } else {
            setIsFetching(false);
            stop.current = true;
        }
    };

    setHistoryMessages = (slug: string, messages: RawMessage[], pageState: string | null, end: boolean) => {
        this.upsertChannelsMsgs(slug, {
            messages,
            pageState,
            end,
        });
        if (!!this.messageCache[slug]) {
            if (this.messageCache[slug]?.pageState === pageState) {
                return;
            } else {
                this.messageCache[slug].messages = [
                    ...(this.messageCache[slug].messages.filter(item => item.id === messages[0]?.id).length === 1
                        ? []
                        : [...messages]),
                    ...this.messageCache[slug].messages,
                ];
                this.messageCache[slug].pageState = pageState;
                this.messageCache[slug].end = end;
            }
        } else {
            // this.messageCache[slug] = {
            //     messages: messages,
            //     pageState: pageState,
            //     end: end,
            //     // @ts-ignore
            //     channelData: this.messageCache[slug].channelData,
            //     // @ts-ignore
            //     channelUsers: this.messageCache[slug]?.channelUsers,
            // };
        }
    };

    // Opens a chat scrolled to a specific message (a global-search result)
    // instead of the newest page, via timestampHistory fetched both
    // directions for context. Replaces any cached entry for this slug
    // outright rather than merging - a fresh anchored view, not a page of
    // the normal history. end:true so the existing scroll-triggered
    // pagination (which only knows the normal pageState cursor) doesn't try
    // to continue from this timestamp-based load; ponytail: no further
    // pagination in an anchored chat - revisit if that's needed later.
    getAnchoredMessages = async (slug: string, timestamp: string, anchorMessageId: string) => {
        runInAction(() => {
            this.isLoadMessages = true;
        });
        try {
            const older = await this.app.chatStore.timestampHistoryOnce(slug, timestamp, true);
            const newer = await this.app.chatStore.timestampHistoryOnce(slug, timestamp, false);
            const merged = [...older.messages.slice().reverse(), ...newer.messages.slice(1)];

            runInAction(() => {
                const messageById = new Map<string, RawMessage>();
                merged.forEach(msg => messageById.set(msg.id, msg));
                this.channelsMsgDataBySlug.set(slug, {
                    messages: messageById,
                    pageState: null,
                    end: true,
                });
                this.messageCache[slug] = {
                    messages: merged,
                    pageState: null,
                    end: true,
                    channelData: this.messageCache[slug]?.channelData,
                    channelUsers: this.messageCache[slug]?.channelUsers,
                };
                this.isLoadMessages = false;
                this.searchMessageId = anchorMessageId;
            });
        } catch (e) {
            runInAction(() => {
                this.isLoadMessages = false;
            });
        }
    };

    addMessageToCache = (message: RawMessage) => {
        this.newMsgChannelsSlug = message.channelSlug;
        this.setChannelMsgNewMessage = message;

        if (this.app.channelStore.selectedChannelData.slug !== message.channelSlug) {
            if (!this.unreadMessages.length || !this.unreadMessages.some(item => item.slug == message.channelSlug)) {
                this.unreadMessages.push({
                    count: 1,
                    slug: message.channelSlug,
                });
            } else {
                let newUnreadM = this.unreadMessages.map(item => {
                    if (item.slug == message.channelSlug) {
                        return {
                            ...item,
                            count: item.count + 1,
                        };
                    } else {
                        return {
                            ...item,
                        };
                    }
                });

                this.unreadMessages = newUnreadM;
            }
        }

        if (this.messageCache[message.channelSlug]) {
            if (_.last(this.messageCache[message.channelSlug].messages)?.id === message.id) {
                runInAction(() => {
                    this.messageCache[message.channelSlug].messages[0] = message;
                });
            } else {
                runInAction(() => {
                    this.messageCache[message.channelSlug].messages.push(message);
                });
            }
        } else {
            runInAction(() => {
                this.messageCache[message.channelSlug] = {
                    messages: [message],
                    pageState: '',
                    channelData: this.messageCache[message.channelSlug]?.channelData,
                    channelUsers: this.messageCache[message.channelSlug]?.channelUsers,
                };
            });
        }
    };

    addMergeMessageToCache = (message: RawMessage) => {
        this.setChannelMessageByMessageId = message;

        if (this.app.channelStore.selectedChannelData.slug !== message.channelSlug) {
            if (!this.unreadMessages.length || !this.unreadMessages.some(item => item.slug == message.channelSlug)) {
                this.unreadMessages.push({
                    count: 1,
                    slug: message.channelSlug,
                });
            } else {
                let newUnreadM = this.unreadMessages.map(item => {
                    if (item.slug == message.channelSlug) {
                        return {
                            ...item,
                            count: item.count + 1,
                        };
                    } else {
                        return {
                            ...item,
                        };
                    }
                });

                this.unreadMessages = newUnreadM;
            }
        }

        runInAction(() => {
            this.messageCache[message.channelSlug].messages.pop();
            this.messageCache[message.channelSlug].messages = [
                ...this.messageCache[message.channelSlug].messages,
                message,
            ];
        });
    };

    setChannelDataCache = (slug: string, channelData: Channel) => {
        if (!this.messageCache[slug]) {
            // this.messageCache[slug] = {
            //     messages: [],
            //     pageState: '',
            //     channelData: channelData,
            //     channelUsers: this.messageCache[slug]?.channelUsers,
            // };
        } else {
            this.messageCache[slug] = {
                ...this.messageCache[slug],
                channelData: channelData,
            };
        }
    };

    setChannelUsersCache = (
        slug: string,
        channelUsers: {
            [key: string]: ChannelsUsersType;
        },
    ) => {
        if (!this.messageCache[slug]) {
            // this.messageCache[slug] = {
            //     messages: [],
            //     pageState: '',
            //     channelData: this.messageCache[slug]?.channelData,
            //     channelUsers: channelUsers,
            // };
        } else {
            this.messageCache[slug] = {
                ...this.messageCache[slug],
                channelUsers: channelUsers,
            };
        }
    };

    getMessagesPageState = (slug: string) => {
        if (this.messageCache[slug]) {
            return this.messageCache[slug].pageState;
        } else {
            return '';
        }
    };

    setMessageText = (text: string) => {
        this.messageTextState = text;
    };

    setMessageImageText = (text: string) => {
        this.messageImageTextState = text;
    };

    setFileType = (type: string) => {
        this.fileType = type;
    };

    exitChannelEmit = () => {
        this.app.chatStore.exitChannel(this.app.channelStore.selectedChannelData.slug);
    };

    setMinRelevance = (minRelevance: number) => {
        this.minRelevance = minRelevance;
        this.setSendMessage = {
            ...this.setSendMessage,
            minRelevance: minRelevance,
        };
    };

    setSendTextMessage = () => {
        if (!!this.addHashtag?.id) {
            this.setSendMessage = {
                ...this.setSendMessage,
                userId: this.app.authStore.user.id,
                channelSlug: this.slug,
                type: 'text',
                message: this.addHashtag.message,
                mentionedUserIds: this.app.channelSocketStore.oneToOneUser.length
                    ? this.app.channelSocketStore.oneToOneUser.map(item => item.id)
                    : [0],
                hashtags: this.addHashtag?.hashtags
                    ? [...this.addHashtag?.hashtags, this.messageTextState.replaceAll('#', '')]
                    : [this.messageTextState.trim().replaceAll('#', '')],
                msgLocation: {
                    latitude: this.app.usersStore.setMyData?.latitude as any,
                    longitude: this.app.usersStore.setMyData?.longitude as any,
                },
            };
        } else {
            this.setSendMessage = {
                ...this.setSendMessage,
                userId: this.app.authStore.user.id,
                channelSlug: this.slug,
                type: 'text',
                mentionedUserIds: this.app.channelSocketStore.oneToOneUser.length
                    ? this.app.channelSocketStore.oneToOneUser.map(item => item.id)
                    : [0],
                message: this.messageTextState,
                msgLocation: {
                    latitude: this.app.usersStore.setMyData?.latitude as any,
                    longitude: this.app.usersStore.setMyData?.longitude as any,
                },
            };
        }
    };

    setSendReplyMessage = () => {
        this.setSendReplyMessageState = {
            isReply: true,
            originMessage: this.setReplyMessage,
            originMessageId: this.setReplyMessage?.id,
            originMessageTimestamp: this.setReplyMessage?.timestamp,
        };
        this.setSendMessage = {
            ...this.setSendMessage,
            ...this.setSendReplyMessageState,
        };
    };

    setSendPhotoMessage = () => {
        if (!!this.addHashtag?.id) {
            this.setSendMessage = {
                ...this.setSendMessage,
                type: 'image',
                userId: this.app.authStore.user.id,
                message: this.messageImageTextState,
                channelSlug: this.slug,
                mediaHeight: this.app.visibleStore.imageFile?.height,
                mediaWidth: this.app.visibleStore.imageFile?.width,
                mediaTitle: this.FileUploadOperation.data.fileTitle,
                mediaUrl: this.FileUploadOperation.data.filePath,
                videoThumbnail: this.FileUploadOperation.data.thumbnailPath,
                mentionedUserIds: this.app.channelSocketStore.oneToOneUser.length
                    ? this.app.channelSocketStore.oneToOneUser.map(item => item.id)
                    : [0],
                hashtags: this.addHashtag?.hashtags
                    ? [...this.addHashtag?.hashtags, this.messageImageTextState.replaceAll('#', '')]
                    : [this.messageImageTextState.trim().replaceAll('#', '')],
            };
        } else {
            this.setSendMessage = {
                ...this.setSendMessage,
                type: 'image',
                userId: this.app.authStore.user.id,
                message: this.messageImageTextState,
                channelSlug: this.slug,
                mediaHeight: this.app.visibleStore.imageFile?.height,
                mediaWidth: this.app.visibleStore.imageFile?.width,
                mediaTitle: this.FileUploadOperation.data.fileTitle,
                mediaUrl: this.FileUploadOperation.data.filePath,
                videoThumbnail: this.FileUploadOperation.data.thumbnailPath,
                mentionedUserIds: this.app.channelSocketStore.oneToOneUser.length
                    ? this.app.channelSocketStore.oneToOneUser.map(item => item.id)
                    : [0],
            };
        }
    };

    setSendVideoMessage = () => {
        this.setSendMessage = {
            ...this.setSendMessage,
            type: 'video',
            userId: this.app.authStore.user.id,
            channelSlug: this.slug,
            mediaTitle: this.FileUploadOperation.data.fileTitle,
            mediaUrl: this.FileUploadOperation.data.filePath,
            videoThumbnail: this.FileUploadOperation.data.thumbnailPath,
        };
    };

    setSendAudioMessage = () => {
        this.setSendMessage = {
            ...this.setSendMessage,
            type: 'audio',
            userId: this.app.authStore.user.id,
            channelSlug: this.slug,
            duration: this.durationMedia,
            mediaTitle: '',
            mediaUrl: this.FileUploadOperation.data.filePath,
            videoThumbnail: this.FileUploadOperation.data.thumbnailPath,
        };
    };

    setSendDocumentMessage = () => {
        this.setSendMessage = {
            ...this.setSendMessage,
            type: 'document',
            userId: this.app.authStore.user.id,
            channelSlug: this.slug,
            mediaTitle: this.FileUploadOperation.data.fileTitle,
            mediaUrl: this.FileUploadOperation.data.filePath,
            videoThumbnail: this.FileUploadOperation.data.thumbnailPath,
        };
    };

    setSendPollMessage = () => {
        this.setSendMessage = {
            ...this.setSendMessage,
            channelSlug: this.slug,
            type: this.pollMessageState.type as never,
            pollType: this.pollMessageState.type as never,
            userId: this.app.authStore.user.id,
            topic: this.pollMessageState.topic,
            options: this.pollMessageOptionState as never,
        };
    };

    onSendPoll = (type?: MessageType) => {
        if (type) {
            this.setSendPollMessage();
        }
        this.app.socketStore.socket?.emit('poll', this.setSendMessage);
    };

    onSendMessage = (type?: MessageType) => {
        switch (type) {
            case 'text':
                this.setSendTextMessage();
                break;
            case 'image':
                this.setSendPhotoMessage();
                break;
            case 'audio':
                this.setSendAudioMessage();
                break;
            case 'video':
                this.setSendVideoMessage();
                break;
            case 'NORMAL':
                this.setSendPollMessage();
                break;
            case 'RELEVANCE':
                this.setSendPollMessage();
                break;
            default:
                break;
        }

        if (this.editMessage) {
            const editedMessage = {
                ...this.editMessage,
                isEdit: true,
                message: this.messageTextState,
            };
            console.log(editedMessage, 'testttt');
            this.onEditMessage(editedMessage);
            this.app.socketStore.socket?.emit('editMessage', editedMessage);
            this.setSendMessage = initialMassegeText;
            this.clearReplyMessage();
            return;
        }
        if (this.addHashtag) {
            const editedMessage = {
                ...this.addHashtag,
                hashtags: this.addHashtag?.hashtags
                    ? [...this.addHashtag?.hashtags, this.messageTextState.trim().replaceAll('#', '')]
                    : [this.messageTextState.trim().replaceAll('#', '')],
            };
            this.onEditMessage(editedMessage);
            this.app.socketStore.socket?.emit('editMessage', editedMessage);
            this.setSendMessage = initialMassegeText;
            this.clearReplyMessage();
            return;
        }

        this.setSendMessage = {
            ...this.setSendMessage,
            hashtags: this.app.hashtagStore.hashTags,
        };
        // console.log('this.setSendMessage', toJS(this.setSendMessage));
        this.app.socketStore.socket?.emit('message', this.setSendMessage);
        this.setSendMessage = initialMassegeText;
        this.messageImageTextState = '';
        this.app.visibleStore.hide('ImagePrewModal');
        this.clearReplyMessage();
        this.app.visibleStore.setImageFile({} as TypeImageFile);
    };

    deleteMessage = (id: string, slug: string, timestamp: Date) => {
        this.app.chatStore.remove({
            id,
            slug,
            timestamp,
        });
        this.onDeleteMessage(id);
    };

    onDeleteMessage = (id: string) => {
        const selectedChannelData = this.getSelectedChannelMsgData;
        if (selectedChannelData) {
            selectedChannelData.messages.delete(id);
        }
    };

    clearReplyMessage = () => {
        this.setReplyMessage = null;
        this.editMessage = null;
        this.addHashtag = null;
    };

    onProgress = (percent: number) => {
        runInAction(() => {
            this.progress = percent;
        });
    };

    readFile = async (file: File, type: SendMessage['type']) => {
        const form = new FormData();
        form.append('file', file, file.name);

        const config = {
            onUploadProgress: (progressEvent: any) => {
                const { loaded, total } = progressEvent;
                var percent = Math.floor((loaded * 100) / total);
                if (percent < 100) {
                    this.onProgress(percent);
                }
            },
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };

        await this.FileUploadOperation.run(() => APIs.upload(form, config));

        if (this.FileUploadOperation.isSuccess) {
            switch (type) {
                case 'audio':
                    this.setSendAudioMessage();
                    break;
                case 'image':
                    this.setSendPhotoMessage();
                    break;
                case 'video':
                    this.setSendVideoMessage();
                    break;
                case 'document':
                    this.setSendDocumentMessage();
                    break;
            }

            this.onSendMessage(type);
        } else {
            console.log('Upload error');
        }
    };

    searchMessage = (messageSearch: string, channelSlug: string) => {
        if (messageSearch.length > 0) return this.app.chatStore.search(messageSearch, channelSlug);
    };

    getPollId = async (pollId: number, includeVotedUsers: boolean) => {
        await this.getPollOperation.run(() => APIs.channels.getPollDetails(pollId, includeVotedUsers));
        runInAction(() => {
            if (this.getPollOperation.isSuccess) {
                this.pollMessageDetails = {
                    ...this.getPollOperation.data,
                } as never;
            }
        });
    };

    setEditMessage = (message: RawMessage) => {
        this.showSetReplyMessage(message);
        this.setMessageText(message.message);
        this.editMessage = message;
    };

    setAddHashtag = (message: RawMessage) => {
        this.showSetReplyMessage(message);
        this.setMessageText('');
        this.addHashtag = message;
    };

    setUnreadMessages = (data: unreadMessagesType[]) => {
        this.unreadMessages = data;
    };

    onEditMessage = (editedMessage: RawMessage) => {
        this.setSelectedChannelMsgById = editedMessage;
    };

    replyMessage = (message: RawMessage) => {
        this.showSetReplyMessage(message);
        this.setSendReplyMessage();
    };

    showSetReplyMessage = (message: RawMessage) => {
        this.setReplyMessage = message;
    };
}
